import moment from "moment-timezone";

/**
 * Reading and writing DSA schedule event times.
 *
 * A timezone-agnostic event stores a fixed wall clock: `startTime` / `endTime` carry the time of day
 * with no timezone attached, and `startDate` / `endDate` carry the same wall clock pinned to UTC. It
 * therefore renders identically in every browser and plays at that wall clock local to whichever
 * screen it runs on.
 *
 * A legacy event stores an absolute instant derived from the creating user's browser timezone, so it
 * still renders in the viewer's timezone. That behavior is deliberately left alone.
 *
 * Every read and write of an event's time goes through this module — no component should do date math
 * on `startDate` directly. See the design spec in the shared repo:
 * docs/superpowers/specs/2026-07-28-dsa-schedule-timezone-agnostic-design.md
 */

/** The time-carrying subset of DsaScheduleEventDto, which a DTO satisfies structurally. */
export type ScheduleEventTimes = {
  startDate?: number;
  endDate?: number;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  timezoneAgnostic?: boolean;
};

/**
 * Encodes a wall clock as the millis we store for a timezone-agnostic event. Pinning to UTC is what
 * makes the value independent of the browser that wrote it.
 *
 * Keep this the only writer: the backend reads the calendar date from these millis and the time of
 * day from `startTime` / `endTime`, so the two must always agree.
 */
export function toStoredMillis(dateStr: string, timeStr: string): number {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes, seconds = 0] = timeStr.split(":").map(Number);
  return Date.UTC(year, month - 1, day, hours, minutes, seconds);
}

/**
 * Encodes a wall clock the way a legacy event stores it: an absolute instant anchored to the timezone
 * the event was created in.
 *
 * Used when re-saving a legacy event, so the round trip is lossless. Reading it back with
 * [eventWallClock] and writing it again through here must not move when the event fires — which it
 * would if the wall clock were re-anchored to whoever happens to be editing.
 */
export function toLegacyMillis(dateStr: string, timeStr: string, timezone?: string): number {
  const zone = knownZone(timezone);
  return zone
    ? moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm:ss", zone).valueOf()
    : new Date(`${dateStr} ${timeStr}`).getTime();
}

/**
 * The wall clock to show for an event — the same times for every viewer, in every timezone.
 *
 * An agnostic event's moments are held in UTC and never converted. A legacy event is read back in the
 * timezone it was created in, which recovers the wall clock its creator actually typed; that is what
 * the device plays it at, and it is stable for everyone else looking at it. Only an event with no
 * usable timezone falls back to the viewer's, as everything used to.
 */
export function eventWallClock(ev: ScheduleEventTimes): { start: moment.Moment; end: moment.Moment } {
  if (ev.timezoneAgnostic) {
    return { start: moment.utc(ev.startDate), end: moment.utc(ev.endDate) };
  }

  const zone = knownZone(ev.timezone);
  return zone
    ? { start: moment.tz(ev.startDate!, zone), end: moment.tz(ev.endDate!, zone) }
    : { start: moment(ev.startDate), end: moment(ev.endDate) };
}

/**
 * The accounts allowed to create timezone-agnostic schedule times, by login email.
 *
 * For anyone else nothing writes the flag, so their new events are stored the old creator-anchored way
 * and the feature is inert for them: the backend never takes its timezone-agnostic branch. Reading is
 * not gated — an agnostic event already in the data renders and plays correctly for everybody.
 *
 * Existing events are converted separately, by the migration in the shared repo
 * (`DsaScheduleTzMigrationOneTimeService`), which has to be run for the same accounts.
 */
export const TIMEZONE_AGNOSTIC_ACCOUNTS = [
  "ian.livadnyi@musthavemenus.com",
  "ian+display@musthavemenus.com",
  "chuck+skykit@musthavemenus.com",
];

/**
 * Whether the signed-in account creates timezone-agnostic events, matched on the account's login
 * email against [TIMEZONE_AGNOSTIC_ACCOUNTS]. Applies in every environment; every other account keeps
 * creating legacy events everywhere.
 *
 * Matching is exact apart from case and surrounding space — no plus-address stripping, since
 * `ian+display@` is a different account from `ian.livadnyi@`.
 */
export function isTimezoneAgnosticEnabledFor(memberEmail?: string): boolean {
  const email = memberEmail?.trim().toLowerCase();
  return !!email && TIMEZONE_AGNOSTIC_ACCOUNTS.includes(email);
}

/**
 * Whether the event being saved should be stored as a fixed wall clock.
 *
 * A new event follows [isTimezoneAgnosticEnabledFor] for whoever is saving it. An existing event keeps
 * whatever it already was, whichever way that goes: editing must not silently re-anchor when an event
 * fires on the device, and an event created by an enabled account keeps working when someone else
 * edits it later.
 */
export function isTimezoneAgnostic(existing?: ScheduleEventTimes, memberEmail?: string): boolean {
  return existing ? existing.timezoneAgnostic === true : isTimezoneAgnosticEnabledFor(memberEmail);
}

/**
 * The millis of a wall-clock moment with its timezone discarded, in the same UTC-pinned encoding
 * [toStoredMillis] produces.
 *
 * Use it to put a legacy and an agnostic event into one comparable frame — the frame the user reads
 * off the screen — for things like overlap detection.
 */
export function wallClockMillis(m: moment.Moment): number {
  return Date.UTC(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
}

/**
 * "Now" as a wall clock in the same frame [eventWallClock] returns, so the two can be compared.
 *
 * Which clock counts depends on what decides playback. An agnostic event plays on the screen's clock,
 * so that is the one read. A legacy event plays at the wall clock its creator typed, in the creator's
 * timezone, so "now" is read there. Either falls back to the viewer's timezone when its own is
 * missing.
 */
export function nowInEventFrame(ev: ScheduleEventTimes, nowMs: number, screenTimezone?: string): number {
  const zone = knownZone(ev.timezoneAgnostic ? screenTimezone : ev.timezone);
  return wallClockMillis(zone ? moment.tz(nowMs, zone) : moment(nowMs));
}

/** The zone name if moment recognises it, otherwise undefined — guards empty and stale zone names. */
function knownZone(timezone?: string): string | undefined {
  return timezone && moment.tz.zone(timezone) ? timezone : undefined;
}
